import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Vec2 } from '~/util/vec2';
import { Title } from '../components/Title/Title';
import type { ReactNode, RefObject } from 'react';

interface TitleActions {
  showForElement: (t: string, el: HTMLElement) => void;
  hide: () => void;
}

const TitleActionsContext = createContext<TitleActions | null>(null);
TitleActionsContext.displayName = 'TitleActions';

/** Delay time between mouse hover and title display, in ms */
const TITLE_TIMEOUT = 250;

export const TitleProvider = ({ children }: { children: ReactNode }) => {
  const [text, setText] = useState<string | null>(null);
  const [titlePos, setTitlePos] = useState(Vec2.zero());

  const showForElement = useCallback((t: string, el: HTMLElement) => {
    const pos = el.getBoundingClientRect();
    setText(t);
    setTitlePos({ x: pos.right, y: pos.bottom });
  }, []);

  const hide = useCallback(() => {
    setText(null);
    setTitlePos(Vec2.zero());
  }, []);

  const actions = useMemo(
    () => ({ showForElement, hide }),
    [hide, showForElement]
  );

  return (
    <TitleActionsContext.Provider value={actions}>
      {children}
      {text && (
        <Title
          text={text}
          pos={{ left: `${titlePos.x}px`, top: `${titlePos.y}px` }}
        />
      )}
    </TitleActionsContext.Provider>
  );
};

/**
 * Hook that attaches a title tooltip to an element, matching the v-title directive behavior.
 * Returns a ref to attach to the target DOM element.
 */
export const useTitleRef = <T extends Element = HTMLElement>(
  titleText: string
): RefObject<T | null> => {
  const actions = useContext(TitleActionsContext);
  if (!actions) {
    throw new Error('TitleProvider not found.');
  }
  const ref = useRef<T>(null);
  const textRef = useRef(titleText);
  textRef.current = titleText;

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let isActive = false;

    function onEnter() {
      timer = setTimeout(() => {
        if (el!.isConnected) {
          isActive = true;
          actions!.showForElement(
            textRef.current,
            el! as unknown as HTMLElement
          );
        }
      }, TITLE_TIMEOUT);
    }

    function onLeave() {
      if (timer) {
        clearTimeout(timer);
      }
      timer = null;
      if (isActive) {
        isActive = false;
        actions!.hide();
      }
    }

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      if (timer) {
        clearTimeout(timer);
      }
      if (isActive) {
        actions.hide();
      }
    };
  }, [actions]);

  return ref;
};
